import { noop } from "lodash";
import {
  ChangeEvent,
  Dispatch,
  SetStateAction,
  SyntheticEvent,
  useEffect,
  useState,
} from "react";
import { Form } from "react-final-form";
import { useDispatch } from "react-redux";
import FormTextField from "@Components/forms/formFields/textField/FormTextField";
import CTFieldValidator, {
  isValidZip,
  minLength,
  required,
} from "@Components/forms/helpers/FieldValidator";
import { billingAddressFieldValidators } from "@Components/forms/helpers/FormValidator";
import { Button } from "@Components/ui";
import { STORAGE_KEYS, STORAGE_TYPES } from "@Constants/storage";
import { getItem, removeItem, setItem } from "@Utils/browserStorage";
import { cartUtilQtyAdd } from "@Utils/cartUtils";
import { formatZipcode } from "@Utils/checkout";
import { isCanadianLocale } from "@Utils/locale";
import { usePickupAvailableForCA } from "@wff/hooks/usePickupAvailableForCA";
import { useZipCode } from "@wff/hooks/useZipCode";
import { useAppSelector } from "@wff/store/hooks";
import { ZipCodeSelector } from "@wff/store/zipcodeSlice";
import classes from "../ZipCodePopUp.module.scss";
interface IInitialLayoutProps {
  initialTitle: string;
  initialMessage: string;
  initialInputPlaceholder: string;
  initialButtonLabel: string;
  zipcode: string;
  switchLayout: Dispatch<SetStateAction<string>>;
  saveZipcode: ({
    inputZipcode,
    availability,
  }: {
    inputZipcode: string;
    availability: boolean;
  }) => void;
}

interface IAddToCartProps {
  isDeliverable: boolean;
  isPickupable?: boolean;
}

export default function InitialLayout({
  initialTitle,
  initialMessage,
  initialInputPlaceholder,
  initialButtonLabel,
  zipcode,
  switchLayout,
}: IInitialLayoutProps) {
  const { handleSubmitZipcode } = useZipCode();
  const [inputZipcode, setInputZipcode] = useState(zipcode);
  const [isLoading, setLoading] = useState(false);
  const { getPickUpAvailable } = usePickupAvailableForCA();
  const [touched, setTouched] = useState(zipcode ? true : false); // if zipcode is initialized then setTouched to true
  const { countryShort, languageCode } = useAppSelector(({ locale }) => locale);
  const isCanadianUser = isCanadianLocale(languageCode);
  const dispatch = useDispatch();
  const { availability } = useAppSelector(ZipCodeSelector);
  const setZipHandler = async (e: ChangeEvent<HTMLInputElement>) => {
    if (!touched) {
      setTouched(true);
    }
    const formattedZipcode = formatZipcode(
      e.target.value.trim(),
      countryShort
    ).trim();

    setInputZipcode(formattedZipcode);
  };

  useEffect(() => {
    if (zipcode) {
      setTouched(true);
      setInputZipcode(zipcode);
    }
  }, [zipcode]);

  /**
   * This method will execute first attemps when user clicks 'continue' button in the PoPUp model
   * after clicks Add To Cart button
   * After stored zipcode information to the local storage this method will not execute.
   */
  const callForAddToCartViaPopUpContinueButtonForCA = ({
    isDeliverable,
    isPickupable,
  }: IAddToCartProps) => {
    const cartId = "";
    const callback = (fn: Function) => {
      if (fn) fn();
    };
    const payloadFromLocalStorageForCA: any = localStorage.getItem(
      STORAGE_KEYS.INCREMENT_PAYLOAD_CA
    );

    let parsedpayloadFromLocalStorageForCA = JSON.parse(
      payloadFromLocalStorageForCA
    );

    parsedpayloadFromLocalStorageForCA = {
      ...parsedpayloadFromLocalStorageForCA,
      availability,
      dispatch,
      callback,
    };

    if (parsedpayloadFromLocalStorageForCA?.sku) {
      if (isDeliverable || isPickupable) {
        cartUtilQtyAdd(cartId, parsedpayloadFromLocalStorageForCA);
      }
      const incrementPayload = getItem(
        STORAGE_KEYS.INCREMENT_PAYLOAD_CA,
        STORAGE_TYPES.LOCAL
      );
      if (incrementPayload) {
        removeItem(STORAGE_KEYS.INCREMENT_PAYLOAD_CA, STORAGE_TYPES.LOCAL);
      }
    }
  };

  const handleButtonClickSubmitZipcode = async (e: SyntheticEvent) => {
    e.preventDefault();
    setLoading(true);
    const isDeliverable = await handleSubmitZipcode(inputZipcode);
    /**
     * added new method to fetch stores and pickupup available information
     */
    const { isPickupable } = await getPickUpAvailable(inputZipcode);
    setLoading(false);
    if (isDeliverable) {
      setItem(
        STORAGE_KEYS.CUSTOMER_ZIP_AVAILABILITY_CA,
        "true",
        STORAGE_TYPES.LOCAL
      );
      switchLayout("weDeliver");
      if (isCanadianUser) {
        callForAddToCartViaPopUpContinueButtonForCA({
          isDeliverable,
          isPickupable,
        });
      }
    } else {
      setItem(
        STORAGE_KEYS.CUSTOMER_ZIP_AVAILABILITY_CA,
        "false",
        STORAGE_TYPES.LOCAL
      );
      callForAddToCartViaPopUpContinueButtonForCA({
        isDeliverable,
        isPickupable,
      });
      switchLayout("weDontDeliver");
    }
  };

  const fieldLength = countryShort === "CA" ? 7 : 5;

  return (
    <div
      className={`${classes["trade-area__content"]}`}
      data-testid="layout--initial-layout"
    >
      <h2 className={classes["trade-area__title"]}>{initialTitle}</h2>
      <p className={classes["trade-area__copy"]}>{initialMessage}</p>
      <Form
        onSubmit={noop}
        render={() => {
          return (
            <form>
              <FormTextField
                name="postalCode"
                onChange={setZipHandler}
                data-testid="form_field-zip"
                type="text"
                placeholder={initialInputPlaceholder}
                maxLength={fieldLength}
                validate={CTFieldValidator(
                  [required(), isValidZip(), minLength(5)],
                  languageCode === "fr-CA" ? "Code Postal" : "Postal Code"
                )}
                value={inputZipcode}
                className={`${classes["trade-area__zip"]} ${
                  !touched && isCanadianUser && classes["trade-area__zip-CA"]
                }`}
                showTooltipIconOnErrorOnly={true}
              />
              {/* Todo:inputZipcodeStatus should be handled for CA only */}
              <Button
                disabled={
                  (touched &&
                    !billingAddressFieldValidators.postalCode(inputZipcode)) ||
                  !touched ||
                  isLoading
                }
                type="submit"
                className="trade-area__action action action--primary"
                onClick={handleButtonClickSubmitZipcode}
              >
                {initialButtonLabel}
              </Button>
            </form>
          );
        }}
      />
    </div>
  );
}
